import { CommonModule, NgTemplateOutlet } from "@angular/common";
import { ChangeDetectionStrategy, Component, ElementRef, ViewChild, ViewContainerRef, computed, effect, inject, input, model, output, signal, viewChild } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { ButtonsModule } from "@progress/kendo-angular-buttons";
import { DialogModule } from "@progress/kendo-angular-dialog";
import { InputsModule } from "@progress/kendo-angular-inputs";
import { DrawerModule } from "@progress/kendo-angular-layout";
import { ContextMenuComponent, ContextMenuModule } from "@progress/kendo-angular-menu";
import { moreVerticalIcon, pencilIcon, plusIcon, trashIcon, xIcon } from "@progress/kendo-svg-icons";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { SessionSummary } from "../models/session";

/** The two actions the per-row menu offers. `action` is what `(select)` is matched on. */
const RENAME = "rename";
const DELETE = "delete";

@Component({
    selector: "lib-sidebar",
    imports: [CommonModule, NgTemplateOutlet, FormsModule, ButtonsModule, DialogModule, InputsModule, DrawerModule, ContextMenuModule, TranslatePipe],
    templateUrl: "./sidebar.component.html",
    styleUrl: "./sidebar.component.scss",
    // Every piece of state here is a signal, so the component never needs an explicit
    // `detectChanges()`. That is not a style preference: the app runs **zoneless** (zone.js sits in
    // the polyfills but `app.config.ts` provides no zone change detection),
    // so a plain field mutated from a callback would update nothing on screen.
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent {
    private readonly translate = inject(TranslateService);

    readonly menuIcon = moreVerticalIcon;
    readonly closeIcon = xIcon;
    readonly newChatIcon = plusIcon;

    /** The conversations to list, newest first, in the order the API already returns them in. */
    readonly sessions = input.required<SessionSummary[]>();

    /** Which row is the conversation currently on screen. */
    readonly activeClientToken = input<string | null>(null);

    /**
     * Whether the three-dots button and its menu are drawn at all. Off for the `web` domain, where
     * the visitor may read their history but not touch it, and where the backend refuses the
     * mutations anyway, since an anonymous identity cannot be verified.
     */
    readonly allowSessionManagement = input(true);

    /**
     * Two-way: the host owns the hamburger, the sidebar closes itself after a pick.
     *
     * Always an overlay `kendo-drawer`: the conversation history is chrome
     * that belongs *inside* the chatbot's own UI, opened from its hamburger icon, regardless of
     * which of the three View Picker views (Floating/Sidebar/Full screen) the chatbot itself is in.
     * The View Picker's "Sidebar" option is a different concept: it docks the *whole chatbot
     * widget* against the host page (the host's own job, via a page-level `kendo-splitter`). It
     * must not be confused with this drawer, which previously and incorrectly switched to a
     * `kendo-splitter` of its own whenever the chatbot's view was "sidebar".
     */
    readonly expanded = model(false);

    /**
     * Where the row's `kendo-contextmenu` mounts its popup. Kendo defaults it to
     * `document.body`, which sits outside `.atl-chatbot-scope` and would render the menu with none
     * of the chatbot's own theme. `AvioblueChatbotComponent` passes down a `ViewContainerRef` anchored inside
     * its own root instead. See `scripts/build-theme.mjs` for the scoping mechanism this
     * avoids escaping.
     */
    readonly appendTo = input<ViewContainerRef | undefined>(undefined);

    readonly newChat = output<void>();
    readonly sessionSelected = output<SessionSummary>();
    readonly sessionRenamed = output<{ id: string; title: string }>();
    readonly sessionDeleted = output<string>();

    /** One reused ContextMenu for every row: `show({left, top})` takes a plain offset. */
    @ViewChild("rowMenu") private rowMenu?: ContextMenuComponent;

    /** The row the three-dots menu was opened from, and therefore the one an action applies to. */
    private readonly menuTarget = signal<SessionSummary | null>(null);

    /**
     * The inline rename input, while one is open.
     *
     * A signal query plus an effect, rather than the `autofocus` attribute: `autofocus` only acts on
     * initial page load, so it would do nothing for an input that appears mid-session, and the lint
     * rule rejects it anyway.
     */
    private readonly renameInput = viewChild<ElementRef<HTMLInputElement>>("renameInput");

    constructor() {
        effect(() => this.renameInput()?.nativeElement.focus());
    }

    /** The row being renamed inline, and the text in its input. */
    readonly renamingId = signal<string | null>(null);
    readonly renameDraft = signal("");

    /** The row a delete has been asked for, held until the confirmation is answered. */
    readonly pendingDelete = signal<SessionSummary | null>(null);

    /**
     * The menu's labels, as a signal.
     *
     * `translate.instant()` is **not** usable here: the ContextMenu takes plain objects rather than
     * a template, so the labels cannot go through the `translate` pipe, and `instant()` returns the
     * key itself until the translation file has loaded, which a `computed` would then cache
     * forever. `stream()` emits again when the catalogue arrives and on every language change.
     */
    private readonly menuLabels = toSignal(this.translate.stream(["rename", "delete"]) as Observable<Record<string, string>>, {
        initialValue: { rename: "Rename", delete: "Delete" },
    });

    readonly menuItems = computed(() => {
        const labels = this.menuLabels();
        return [
            { text: labels["rename"], svgIcon: pencilIcon, action: RENAME },
            { text: labels["delete"], svgIcon: trashIcon, action: DELETE, cssClass: "menu-danger" },
        ];
    });

    close(): void {
        this.expanded.set(false);
    }

    onNewChat(): void {
        this.cancelRename();
        this.newChat.emit();
        this.close();
    }

    onSelect(session: SessionSummary): void {
        this.sessionSelected.emit(session);
        this.close();
    }

    /**
     * Opens the row menu at the button that was clicked.
     *
     * Positioned by offset rather than by `[target]` because a single ContextMenu instance serves
     * every row: one instance per row would mean as many popups as conversations.
     */
    openRowMenu(event: MouseEvent, session: SessionSummary): void {
        event.stopPropagation();
        this.menuTarget.set(session);

        const button = event.currentTarget as HTMLElement;
        const rect = button.getBoundingClientRect();
        this.rowMenu?.show({ left: rect.left, top: rect.bottom });
    }

    onMenuSelect(event: { item: { action?: string } }): void {
        const session = this.menuTarget();
        if (!session) {
            return;
        }

        if (event.item.action === RENAME) {
            this.startRename(session);
        } else if (event.item.action === DELETE) {
            this.pendingDelete.set(session);
        }
    }

    startRename(session: SessionSummary): void {
        this.renamingId.set(session.id);
        this.renameDraft.set(session.title);
    }

    cancelRename(): void {
        this.renamingId.set(null);
        this.renameDraft.set("");
    }

    /**
     * Commits an inline rename.
     *
     * A title that is blank or unchanged is dropped rather than sent: the backend refuses a
     * whitespace-only title with a 422, and re-sending the same string would be a request that
     * changes nothing.
     */
    commitRename(session: SessionSummary): void {
        const title = this.renameDraft().trim();
        this.cancelRename();

        if (!title || title === session.title) {
            return;
        }
        this.sessionRenamed.emit({ id: session.id, title });
    }

    confirmDelete(): void {
        const session = this.pendingDelete();
        this.pendingDelete.set(null);
        if (session) {
            this.sessionDeleted.emit(session.id);
        }
    }

    cancelDelete(): void {
        this.pendingDelete.set(null);
    }

    /**
     * The subtitle under each title.
     *
     * The API returns UTC (`firstMessageAt` carries a `Z`); `toLocaleString` renders it in the
     * browser's own zone, which is the project rule for every timestamp that reaches a screen.
     */
    formatTimestamp(value: string): string {
        return new Date(value).toLocaleString(this.translate.currentLang, {
            dateStyle: "short",
            timeStyle: "short",
        });
    }
}
